/**
 * A grade group's reimbursable meals claimed, as a card of its own under the
 * group's table (PROD-1441, Abner 2026-10-08): the main choices' Served,
 * Reimbursable and Non-reimbursable, each figure under its own title. The
 * desktop table and the phone's review show this one card, for every grade
 * group that has main choices.
 *
 * Read-only: the figures are sums of the counts typed above, so they move as
 * the counts do. A figure nobody has entered yet shows the empty-value dash.
 * The card is white, not the item rows' tint (Abner 2026-10-08).
 */

import { cn } from '@ordo/ui/cn'

import type { RecordSpreadsheetClaimedView } from '@/components/production/spreadsheet/record-spreadsheet-table'
import { EMPTY_VALUE_TEXT } from '@/lib/production-records/mobile-review'

/** The card's title and its one-line explanation. */
export const CLAIMED_TITLE = 'Reimbursable meals claimed'
export const CLAIMED_CAPTION = 'Main choices served'

/** The three figures in the table's column order, each with its title. */
export const CLAIMED_FIGURES: ReadonlyArray<{ key: keyof RecordSpreadsheetClaimedView; label: string }> = [
  { key: 'served', label: 'Served' },
  { key: 'reimbursable', label: 'Reimbursable' },
  { key: 'nonReimbursable', label: 'Non-reimbursable' },
]

export function ClaimedMealsCard({
  claimed,
  className,
}: {
  claimed: RecordSpreadsheetClaimedView
  className?: string
}) {
  return (
    <div
      data-claimed-card
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-10 gap-y-3 rounded-[10px] border bg-card px-5 py-4',
        className,
      )}>
      <div>
        <p className="text-sm font-semibold leading-5">{CLAIMED_TITLE}</p>
        <p className="text-xs leading-4 text-muted-foreground">{CLAIMED_CAPTION}</p>
      </div>
      <dl className="grid grid-cols-3 gap-x-6">
        {CLAIMED_FIGURES.map((figure) => (
          <div key={figure.key} data-claimed-figure={figure.key}>
            <dt className="text-xs leading-4 text-muted-foreground">{figure.label}</dt>
            <dd className="mt-1 text-xl font-semibold leading-7 tabular-nums">
              {claimed[figure.key] ?? EMPTY_VALUE_TEXT}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
